/**
 * As regras PURAS da lista de clientes: juntar as três leituras e ordenar a escolha do atendimento.
 *
 * Porte literal de `carregarMapaClientesLead` (`portal/admin.html:3541-3614`) e de
 * `renderizarSelecaoLeadAdmin` (`admin.html:5891-5900`). Sem banco, sem relógio e sem tela, para a
 * mesma entrada dar a mesma lista que o legado dá, inclusive nos casos de borda.
 */

// Os tipos das três leituras, como o módulo de dados as devolve.
import type { LeadDaLista, LeadParaEscolha, PerfilDaLista, QuestionarioDaLista } from '@studiolu/dados/areas/clientes';

/** Como a cliente chegou à lista (`origemCliente`, `admin.html:3597-3601`). */
export type OrigemDaCliente = 'funil' | 'admin' | 'proprio';

/** Uma cliente da lista, com os mesmos campos do objeto do legado (`admin.html:3603-3613`). */
export interface ClienteDaLista {
  readonly user_id: string;
  // O e-mail do lead vinculado, e na falta dele o de login.
  readonly email: string | null;
  // A data do cadastro no portal, que a lista mostra como "cadastrada em".
  readonly criado_em: string;
  // O nome do lead, e na falta dele o do questionário geral.
  readonly nome_completo: string | null;
  // O WhatsApp do lead, e na falta dele o telefone do questionário.
  readonly telefone: string | null;
  // Sem nenhuma linha de questionário geral: a lista escreve "questionário pendente".
  readonly questionario_pendente: boolean;
  readonly origem: OrigemDaCliente;
}

/** Os produtos de uma cliente, separados como o legado separa (`admin.html:3586-3589`). */
export interface ProdutosDaCliente {
  // De leads em andamento, na ordem em que apareceram (a ordem de inserção do `Set` do legado).
  readonly contratados: readonly string[];
  // De leads finalizados.
  readonly finalizados: readonly string[];
}

/** O resultado da junção: a lista e os selos de produto de cada cliente. */
export interface ListaDeClientes {
  readonly clientes: readonly ClienteDaLista[];
  readonly produtosPorCliente: ReadonlyMap<string, ProdutosDaCliente>;
}

/**
 * Junta perfis, questionários e leads na lista de clientes.
 *
 * Três detalhes do legado que esta função preserva, e cada um muda o que aparece na tela:
 *   · o questionário que vale é o ÚLTIMO que o banco devolveu para a cliente (`forEach` que
 *     sobrescreve, `admin.html:3561`);
 *   · nome, e-mail e telefone vêm do PRIMEIRO lead que os tem (`!emailPorCliente[...]`, `3580-3582`),
 *     inclusive de lead perdido, que só fica de fora dos selos de produto;
 *   · a origem é "funil" para qualquer cliente com lead, mesmo perdido (`temLead`, `3579`).
 */
export function montarClientes(
  perfis: readonly PerfilDaLista[],
  questionarios: readonly QuestionarioDaLista[],
  leads: readonly LeadDaLista[],
): ListaDeClientes {
  // O questionário de cada cliente; o último da lista vence, como o `forEach` do legado.
  const questionarioPorUserId = new Map<string, QuestionarioDaLista>();
  for (const q of questionarios) questionarioPorUserId.set(q.user_id, q);

  // Contato vindo do Funil, o primeiro valor encontrado de cada campo.
  const emailPorCliente = new Map<string, string>();
  const nomePorCliente = new Map<string, string>();
  const telefonePorCliente = new Map<string, string>();
  // Quem tem ao menos um lead, perdido ou não.
  const temLead = new Set<string>();
  // Os selos de produto; `Set` guarda a ordem de inserção e ignora repetição, como no legado.
  const grupos = new Map<string, { contratados: Set<string>; finalizados: Set<string> }>();

  for (const lead of leads) {
    // Lead sem cliente vinculada não entra (`if (!lead.user_id_vinculado) return;`).
    const userId = lead.user_id_vinculado;
    if (!userId) continue;
    temLead.add(userId);
    // Texto vazio não conta como valor, como o `lead.email &&` do legado.
    if (lead.email && !emailPorCliente.has(userId)) emailPorCliente.set(userId, lead.email);
    if (lead.nome && !nomePorCliente.has(userId)) nomePorCliente.set(userId, lead.nome);
    if (lead.contato_whatsapp && !telefonePorCliente.has(userId)) telefonePorCliente.set(userId, lead.contato_whatsapp);
    // O grupo nasce para toda cliente com lead, mesmo que só tenha lead perdido.
    const grupo = grupos.get(userId) ?? { contratados: new Set<string>(), finalizados: new Set<string>() };
    grupos.set(userId, grupo);
    // Finalizada vai para os finalizados, perdido não entra, e o resto está contratado.
    const alvo = lead.status === 'finalizada' ? grupo.finalizados : lead.status === 'perdido' ? null : grupo.contratados;
    if (alvo) for (const produto of lead.produtos ?? []) alvo.add(produto);
  }

  // A origem, pela ordem de prioridade do legado: Funil, depois cadastro pela admin, depois próprio.
  const origemDe = (userId: string): OrigemDaCliente => {
    if (temLead.has(userId)) return 'funil';
    if (questionarioPorUserId.get(userId)?.origem_cadastro === 'admin') return 'admin';
    return 'proprio';
  };

  // Uma cliente por perfil, na ordem em que os perfis vieram (mais recente primeiro).
  const clientes = perfis.map<ClienteDaLista>((p) => ({
    user_id: p.user_id,
    // `||` e não `??`: o legado troca também o texto vazio pelo e-mail de login.
    email: emailPorCliente.get(p.user_id) || p.email,
    criado_em: p.criado_em,
    nome_completo: nomePorCliente.get(p.user_id) || questionarioPorUserId.get(p.user_id)?.nome_completo || null,
    telefone: telefonePorCliente.get(p.user_id) || questionarioPorUserId.get(p.user_id)?.telefone || null,
    questionario_pendente: !questionarioPorUserId.has(p.user_id),
    origem: origemDe(p.user_id),
  }));

  // Os grupos viram listas congeladas, para a tela não mexer no que a junção decidiu.
  const produtosPorCliente = new Map<string, ProdutosDaCliente>();
  for (const [userId, grupo] of grupos) {
    produtosPorCliente.set(userId, { contratados: [...grupo.contratados], finalizados: [...grupo.finalizados] });
  }
  return { clientes, produtosPorCliente };
}

/**
 * Os atendimentos na ordem do modal "Qual atendimento?": em andamento primeiro, finalizados depois,
 * mantendo entre iguais a ordem que veio do banco (mais novo primeiro). `sort` é estável, como o
 * do legado (`admin.html:5895-5899`).
 */
export function ordenarLeadsParaEscolha(leads: readonly LeadParaEscolha[]): LeadParaEscolha[] {
  // 0 para em andamento e 1 para finalizada: a subtração põe os em andamento na frente.
  const peso = (l: LeadParaEscolha) => (l.status !== 'finalizada' ? 0 : 1);
  return [...leads].sort((a, b) => peso(a) - peso(b));
}
